import type { AttendanceImportResult, AttendanceImportSession } from '@erp/shared';
import { FileUp } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Button, Card, DataTable, type DataTableColumn, FormField } from '../../../components/ui/index.js';
import { attendanceErrorCode, importAttendance } from '../../../lib/attendance.js';

const STATUS_TONE: Record<AttendanceImportSession['status'], 'success' | 'neutral' | 'info' | 'danger'> = {
  new: 'success',
  existing: 'neutral',
  closes: 'info',
  conflict: 'danger',
};

/**
 * The fingerprint device's punch log, brought in: pick the export (the device's
 * own AttLog.dat, or a CSV/Excel from its software), preview what it will do,
 * then apply. Importing the same file twice adds nothing, and a later export
 * fills in the check-outs an earlier one was missing.
 */
export function AttendanceImportPanel({ onApplied }: { onApplied?: () => void }): JSX.Element {
  const { t } = useTranslation('hr');
  const [file, setFile] = useState<File | null>(null);
  const [dateOrder, setDateOrder] = useState<'dmy' | 'mdy'>('dmy');
  const [trustState, setTrustState] = useState(false);
  const [result, setResult] = useState<AttendanceImportResult | null>(null);
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null);

  const run = async (dryRun: boolean): Promise<void> => {
    if (!file) return;
    setBusy(dryRun ? 'preview' : 'apply');
    try {
      const r = await importAttendance(await file.arrayBuffer(), { dryRun, dateOrder, useState: trustState });
      setResult(r);
      if (!dryRun) {
        toast.success(t('attendance.import.applied', { added: r.counts.new, closed: r.counts.closes }));
        onApplied?.();
      }
    } catch (err) {
      const code = attendanceErrorCode(err);
      toast.error(t(`attendance.import.errors.${code?.replace('attendance_import.', '') ?? 'generic'}`, {
        defaultValue: t('attendance.errors.generic'),
      }));
    } finally {
      setBusy(null);
    }
  };

  const columns: DataTableColumn<AttendanceImportSession>[] = [
    {
      id: 'employee',
      header: t('attendance.columns.employee'),
      sortValue: (s) => s.displayName,
      searchValue: (s) => `${s.code} ${s.displayName}`,
      cell: (s) => (<span><span className="font-mono text-xs text-fg-muted">{s.code}</span> <span className="font-medium">{s.displayName}</span></span>),
    },
    { id: 'date', header: t('attendance.board.date'), sortValue: (s) => s.workDate, cellClassName: 'font-mono text-xs', cell: (s) => s.workDate },
    { id: 'in', header: t('attendance.columns.in'), sortValue: (s) => s.checkIn, cellClassName: 'font-mono text-sm', cell: (s) => s.checkIn.slice(11) },
    { id: 'out', header: t('attendance.columns.out'), sortValue: (s) => s.checkOut ?? '', cellClassName: 'font-mono text-sm', cell: (s) => s.checkOut?.slice(11) ?? '—' },
    {
      id: 'status',
      header: t('attendance.columns.status'),
      sortValue: (s) => s.status,
      cell: (s) => <Badge tone={STATUS_TONE[s.status]}>{t(`attendance.import.status.${s.status}`)}</Badge>,
    },
  ];

  const stat = (label: string, value: number, tone = ''): JSX.Element => (
    <div className="rounded-lg border border-border bg-bg-subtle px-3 py-2">
      <div className="truncate text-[0.7rem] text-fg-muted">{label}</div>
      <div className={`font-mono text-xl font-semibold tabular-nums ${value > 0 ? tone : 'text-fg-subtle'}`}>{value}</div>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-col gap-4 p-5">
        <p className="text-sm text-fg-muted">{t('attendance.import.description')}</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField id="imp-file" label={t('attendance.import.file')}>
            <input
              id="imp-file"
              type="file"
              accept=".xlsx,.csv,.txt,.dat,.tsv"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); }}
              className="block w-full text-sm file:me-3 file:rounded-md file:border file:border-border file:bg-bg-subtle file:px-3 file:py-1.5 file:text-sm file:text-fg"
            />
          </FormField>
          <FormField id="imp-order" label={t('attendance.import.dateOrder')}>
            <select
              id="imp-order"
              className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
              value={dateOrder}
              onChange={(e) => { setDateOrder(e.target.value as 'dmy' | 'mdy'); setResult(null); }}
            >
              <option value="dmy">{t('attendance.import.dmy')}</option>
              <option value="mdy">{t('attendance.import.mdy')}</option>
            </select>
          </FormField>
          <label className="flex items-start gap-2 self-end text-sm">
            <input type="checkbox" className="mt-0.5 h-4 w-4" checked={trustState} onChange={(e) => { setTrustState(e.target.checked); setResult(null); }} />
            <span>
              <span className="font-medium">{t('attendance.import.useState')}</span>
              <span className="block text-xs text-fg-muted">{t('attendance.import.useStateHint')}</span>
            </span>
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void run(true)} disabled={!file} loading={busy === 'preview'} className="gap-1.5">
            <FileUp className="h-4 w-4" aria-hidden />
            {t('attendance.import.preview')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => void run(false)}
            // Only what was just previewed may be applied.
            disabled={!file || !result || !result.dryRun || (result.counts.new === 0 && result.counts.closes === 0)}
            loading={busy === 'apply'}
          >
            {t('attendance.import.apply')}
          </Button>
        </div>
      </Card>

      {result && (
        <>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-9">
            {stat(t('attendance.import.punches'), result.punches)}
            {stat(t('attendance.import.employees'), result.employees)}
            {stat(t('attendance.import.status.new'), result.counts.new, 'text-success')}
            {stat(t('attendance.import.status.closes'), result.counts.closes, 'text-info')}
            {stat(t('attendance.import.status.existing'), result.counts.existing)}
            {stat(t('attendance.import.status.conflict'), result.counts.conflict, 'text-danger')}
            {stat(t('attendance.import.doubleScans'), result.doubleScans)}
            {stat(t('attendance.import.unpaired'), result.unpaired, 'text-warning')}
            {stat(t('attendance.import.untracked'), result.skippedUntracked)}
          </div>
          {result.dryRun && <p className="text-xs text-fg-muted">{t('attendance.import.previewNote')}</p>}
          {result.unknownIds.length > 0 && (
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-warning">{t('attendance.import.unknown')}</h3>
              <p className="mt-1 text-xs text-fg-muted">{t('attendance.import.unknownHint')}</p>
              <ul className="mt-2 flex flex-wrap gap-2 text-sm">
                {result.unknownIds.map((u) => (
                  <li key={u.id} className="rounded-md border border-border px-2 py-1">
                    <span className="font-mono">{u.id}</span>{u.name ? ` · ${u.name}` : ''} <span className="text-fg-muted">×{u.punches}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {result.badRows.length > 0 && (
            <p className="text-xs text-danger">{t('attendance.import.badRows', { rows: result.badRows.join(', ') })}</p>
          )}
          {result.sessions.length > 0 && (
            <Card className="overflow-hidden p-0">
              <DataTable columns={columns} data={result.sessions} rowKey={(s) => `${s.employeeId}-${s.checkIn}`} pageSize={25} searchPlaceholder={t('attendance.board.search')} />
            </Card>
          )}
        </>
      )}
    </div>
  );
}
